import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

import { WINDOW } from '@hglobal/fin-ui';

@Injectable({ providedIn: 'root' })
export class ViewportService {
  readonly #window = inject(WINDOW);
  readonly #document = inject(DOCUMENT);
  readonly #platformId = inject(PLATFORM_ID);

  readonly #baseContent = 'width=device-width, initial-scale=1';

  isLandscape = signal(false);

  boundCheckOrientation = this.#checkOrientation.bind(this);

  private get meta(): HTMLMetaElement | null {
    if (!isPlatformBrowser(this.#platformId)) return null;
    return this.#document.querySelector('meta[name="viewport"]');
  }

  applyViewportForPlatform(): void {
    if (!this.meta) return;

    if (this.#isIos()) {
      // Add maximum-scale=1 ONLY for iOS to disable inputs zooming
      this.meta.setAttribute(
        'content',
        `${this.#baseContent}, maximum-scale=1`,
      );
    } else {
      // Ensure it does NOT have maximum-scale elsewhere
      this.meta.setAttribute('content', this.#baseContent);
    }
  }

  applyOrientationLock(): void {
    this.#checkOrientation();

    window.addEventListener('resize', this.boundCheckOrientation);
    window.addEventListener('orientationchange', this.boundCheckOrientation);
  }

  destroyOrientationLock(): void {
    window.removeEventListener('resize', this.boundCheckOrientation);
    window.removeEventListener('orientationchange', this.boundCheckOrientation);
    this.#document.body.classList.remove('landscape-lock');
  }

  #isIos(): boolean {
    if (!isPlatformBrowser(this.#platformId)) return false;

    const ua = window.navigator.userAgent || '';
    const isIOS = /iP(ad|hone|od)/.test(ua);
    const isWebkit = /WebKit/.test(ua);
    const isChrome = /CriOS/.test(ua);
    const isFirefox = /FxiOS/.test(ua);

    return isIOS && (isWebkit || isChrome || isFirefox);
  }

  #checkOrientation(): void {
    const isTouch =
      'ontouchstart' in this.#window ||
      this.#window.navigator.maxTouchPoints > 0;

    const isCoarsePointer = matchMedia('(pointer: coarse)').matches;

    const isPhoneSize = matchMedia('(max-device-width: 900px)').matches;

    const isPhone = isTouch && isCoarsePointer && isPhoneSize;

    const isLandscape =
      matchMedia('(orientation: landscape)').matches && isPhone;

    this.isLandscape.set(isLandscape);

    document.body.classList.toggle('landscape-lock', isLandscape);
  }
}
